import { ApiError, classifyStatus, toApiError } from './errors';

const RAW_BASE = import.meta.env.VITE_API_BASE_URL ?? '';
export const API_ORIGIN = RAW_BASE.replace(/\/+$/, '');

/**
 * 🔴 TWO base paths, because the API has two.
 *
 * `/auth/*` is mounted at the ROOT (`authRoutes` is registered with no prefix in the API's app.ts);
 * everything else lives under `/api/v1`. Assuming one prefix for both is what this app did first,
 * and the symptom was a 404 on `/api/v1/auth/me` that presented as "not signed in".
 */
export const API_V1 = `${API_ORIGIN}/api/v1`;
export const API_ROOT = API_ORIGIN;

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
  /** Defaults to the `/api/v1` surface. Auth endpoints pass `API_ROOT`. */
  base?: string;
}

export function buildUrl(base: string, path: string, query?: Query): string {
  const url = new URL(`${base}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === null || value === undefined || value === '') continue;
    url.searchParams.set(key, String(value));
  }
  return url.toString();
}

/**
 * Pull a human-readable reason out of an error body.
 *
 * The API's envelope is `{ error: 'code', message: 'text' }` — and for a 5xx the message is
 * deliberately fixed, because a Postgres error carries the failing SQL. We show what it gives us and
 * never assume more.
 */
function extractReason(body: unknown): string | null {
  if (typeof body === 'string') return body.trim() ? body.trim().slice(0, 500) : null;
  if (!body || typeof body !== 'object') return null;
  const record = body as Record<string, unknown>;
  for (const candidate of [record.message, record.reason, record.detail, record.error]) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim().slice(0, 500);
    if (candidate && typeof candidate === 'object') {
      const nested = candidate as Record<string, unknown>;
      for (const key of ['reason', 'message', 'detail'] as const) {
        const value = nested[key];
        if (typeof value === 'string' && value.trim()) return value.trim().slice(0, 500);
      }
    }
  }
  return null;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/**
 * The single place a network call is made.
 *
 * `credentials: 'include'` because the session is an httpOnly cookie set by the API on a different
 * origin (`planhub-api.tradebeam.xyz` vs `planhub.tradebeam.xyz`) — the token is never in JS reach,
 * so it cannot be read by an XSS.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = buildUrl(options.base ?? API_V1, path, options.query);
  let response: Response;

  try {
    response = await fetch(url, {
      method: options.method ?? 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal ?? null,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw toApiError(err, url);
  }

  if (!response.ok) {
    const body = await readBody(response);
    throw new ApiError({
      kind: classifyStatus(response.status),
      status: response.status,
      reason: extractReason(body),
      url,
    });
  }

  if (response.status === 204) return null as T;
  return (await readBody(response)) as T;
}

export function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}
